import { BLOG_STATUS_DRAFT, BLOG_STATUS_PUBLISHED, excerptOf, readMinutes, stripHtml, type BlogStatus } from '@iws/theme-schema/blog'
import { AlertCircle, Eye, ImagePlus, Trash2, X } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useEffect, useState } from 'react'

import { getFileUrl } from '@/clients/http'
import { useBlogBody, useBlogPost, useDeleteBlogPost, useSaveBlogPost } from '@/hooks/features/blog'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import RichTextEditor from '@/components/ui/RichTextEditor'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

import { BackButton, ConfirmDialog, PageHeader } from '@/components/atoms'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'

import { BlogStatusBadge } from './BlogStatusBadge'
import { Spinner } from '@/components/ui/spinner'

const MIN_TITLE = 5
const EXCERPT_MAX = 300
const SEO_TITLE_MAX = 70
const SEO_DESCRIPTION_MAX = 160
const TAGS_MAX = 10

/** An ISO time as the value of a datetime-local input (local time, minutes). */
const toLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

/** Comma-separated tags: trimmed, empty ones dropped, duplicates (ignoring case) removed, at most TAGS_MAX. */
const parseTags = (text: string): string[] => {
  const seen = new Set<string>()
  return text
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t && !seen.has(t.toLowerCase()) && seen.add(t.toLowerCase()))
    .slice(0, TAGS_MAX)
}

/** What the shopper sees after /blog/: lower case, letters and digits of any script joined by single hyphens. */
const cleanSlug = (value: string, final = false): string => {
  const slug = value.toLowerCase().replace(/['’]/g, '').replace(/[^\p{L}\p{M}\p{N}]+/gu, '-').replace(/^-+/, '')
  return final ? slug.replace(/-+$/, '') : slug
}

interface Banner {
  id: string
  path: string
}

export const BlogEditorPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const post = useBlogPost(id)
  const body = useBlogBody(id, post.data?.content_id)
  const save = useSaveBlogPost()
  const remove = useDeleteBlogPost()

  const [title, setTitle] = useState('')
  const [html, setHtml] = useState('')
  const [banner, setBanner] = useState<Banner | null>(null)
  const [readDuration, setReadDuration] = useState('')
  const [slug, setSlug] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [category, setCategory] = useState('')
  const [tagsText, setTagsText] = useState('')
  const [authorName, setAuthorName] = useState('')
  const [seoTitle, setSeoTitle] = useState('')
  const [seoDescription, setSeoDescription] = useState('')
  const [publishAt, setPublishAt] = useState('')
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<BlogStatus | null>(null)

  useEffect(() => {
    if (!post.data) return
    setTitle(post.data.title)
    setReadDuration(post.data.read_duration ?? '')
    setSlug(post.data.slug ?? '')
    setExcerpt(post.data.excerpt ?? '')
    setCategory(post.data.category ?? '')
    setTagsText((post.data.tags ?? []).join(', '))
    setAuthorName(post.data.author_name ?? '')
    setSeoTitle(post.data.seo_title ?? '')
    setSeoDescription(post.data.seo_description ?? '')
    setPublishAt(toLocalInput(post.data.published_at))
    setBanner(
      post.data.banner_id && post.data.banner?.path ? { id: post.data.banner_id, path: post.data.banner.path } : null
    )
  }, [post.data])

  useEffect(() => {
    if (body.data === undefined) return
    setHtml(body.data)
    // A stored read time equal to the estimate was filled in automatically: show it as "estimated" again.
    if (post.data?.read_duration && Number(post.data.read_duration) === readMinutes(body.data)) setReadDuration('')
  }, [body.data, post.data?.read_duration])

  const status = post.data?.status
  const published = status === BLOG_STATUS_PUBLISHED
  const scheduled = Boolean(publishAt) && new Date(publishAt).getTime() > Date.now()

  const submit = (next: BlogStatus) => {
    if (title.trim().length < MIN_TITLE) {
      toast.error(`The title needs at least ${MIN_TITLE} characters`)
      return
    }
    if (!stripHtml(html)) {
      toast.error('Write the post body first')
      return
    }
    setPendingStatus(next)
    save.mutate(
      {
        id,
        title,
        html,
        bannerId: banner?.id ?? null,
        // Empty fields are filled from the body so list cards never need to read it.
        readDuration: readDuration.trim() || String(readMinutes(html)),
        excerpt: excerpt.trim() || excerptOf(html, 200),
        slug: cleanSlug(slug, true) === post.data?.slug ? '' : cleanSlug(slug, true),
        category,
        tags: parseTags(tagsText),
        authorName,
        seoTitle,
        seoDescription,
        publishAt: publishAt ? new Date(publishAt).toISOString() : null,
        status: next,
        previousContentId: post.data?.content_id,
      },
      {
        onSuccess: ({ post: saved, staleBody }) => {
          toast.success(next !== BLOG_STATUS_PUBLISHED ? 'Draft saved' : scheduled ? 'Post scheduled' : 'Post published')
          if (staleBody) toast.warning('The previous version of the text could not be removed from the media library.')
          if (!id) navigate(`/blog/${saved._id}`, { replace: true })
        },
        onError: (e) => toast.error(e.message),
        onSettled: () => setPendingStatus(null),
      }
    )
  }

  const confirmDelete = () => {
    if (!id) return
    remove.mutate(id, {
      onSuccess: () => {
        toast.success('Post moved to the trash')
        navigate('/blog')
      },
      onError: (e) => toast.error(e.message),
    })
  }

  const back = <BackButton onClick={() => navigate('/blog')} label="Back to posts" />

  if (id && post.isError) {
    return (
      <div className="mx-auto w-full max-w-[1088px]">
        <PageHeader size="large" title="Edit post" back={back} />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Could not load the post</AlertTitle>
          <AlertDescription>{post.error.message}</AlertDescription>
        </Alert>
      </div>
    )
  }

  const loading = Boolean(id) && (post.isLoading || body.isLoading)
  const busy = save.isPending || remove.isPending

  return (
    <div className="mx-auto w-full max-w-[1088px] pb-20">
      <PageHeader
        size="large"
        title={id ? 'Edit post' : 'New post'}
        back={back}
        badge={status && <BlogStatusBadge status={status} publishedAt={post.data?.published_at} />}
        actions={
          <>
            {id && (
              <>
                <Button variant="ghost" size="icon" aria-label="Delete post" disabled={busy} onClick={() => setDeleteOpen(true)}>
                  <Trash2 className="size-4 text-error" />
                </Button>
                <Button variant="secondary" onClick={() => navigate(`/blog/${id}/preview`)}>
                  <Eye className="size-4" />
                  Preview
                </Button>
              </>
            )}
            <Button variant="secondary" disabled={busy || loading} onClick={() => submit(BLOG_STATUS_DRAFT)}>
              {pendingStatus === BLOG_STATUS_DRAFT && <Spinner className="size-4" />}
              {published ? 'Unpublish' : 'Save draft'}
            </Button>
            <Button disabled={busy || loading} onClick={() => submit(BLOG_STATUS_PUBLISHED)}>
              {pendingStatus === BLOG_STATUS_PUBLISHED && <Spinner className="size-4" />}
              {scheduled ? 'Schedule' : published ? 'Update' : 'Publish'}
            </Button>
          </>
        }
      />

      {body.isError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="size-4" />
          <AlertTitle>Could not load the post body</AlertTitle>
          <AlertDescription>{body.error.message}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_288px] lg:gap-6">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_288px] lg:gap-6">
          <div className="min-w-0 space-y-4">
            <Card>
              <CardContent className="space-y-4 pt-4">
                <div className="space-y-1">
                  <Label htmlFor="blog-title">Title</Label>
                  <Input
                    id="blog-title"
                    value={title}
                    maxLength={200}
                    placeholder="e.g. Blog about your latest products or deals"
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="blog-slug">Address</Label>
                  <div className="flex h-8 items-center rounded-md border border-input bg-card focus-within:ring-2 focus-within:ring-ring">
                    <span className="shrink-0 pl-3 text-sm text-muted-foreground">/blog/</span>
                    <input
                      id="blog-slug"
                      value={slug}
                      maxLength={80}
                      placeholder={id ? '' : 'made from the title'}
                      className="h-full min-w-0 flex-1 bg-transparent pr-3 text-sm text-ink outline-none"
                      aria-describedby="blog-slug-help"
                      onChange={(e) => setSlug(cleanSlug(e.target.value))}
                      onBlur={() => setSlug(cleanSlug(slug, true))}
                    />
                  </div>
                  <p id="blog-slug-help" className="text-sm text-text-2">
                    {published ? 'Changing it breaks links people already shared.' : 'Stays the same when you change the title.'}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="blog-excerpt">Excerpt</Label>
                  <Textarea
                    id="blog-excerpt"
                    value={excerpt}
                    maxLength={EXCERPT_MAX}
                    rows={3}
                    placeholder={excerptOf(html, 200) || 'A sentence or two for the blog list and search results'}
                    aria-describedby="blog-excerpt-help"
                    onChange={(e) => setExcerpt(e.target.value)}
                  />
                  <p id="blog-excerpt-help" className="text-sm text-text-2">
                    {excerpt.length}/{EXCERPT_MAX} · Shown on the blog list and in search results. Leave empty to use the first paragraph.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Content</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RichTextEditor value={html} onChange={setHtml} placeholder="Write your post…" />
                <div className="space-y-1 border-t border-border-subtle pt-4">
                  <Label htmlFor="blog-read">Read time (minutes)</Label>
                  <Input
                    id="blog-read"
                    type="number"
                    min={1}
                    className="sm:w-48"
                    value={readDuration}
                    placeholder={`${readMinutes(html)} (estimated)`}
                    aria-describedby="blog-read-help"
                    onChange={(e) => setReadDuration(e.target.value)}
                  />
                  <p id="blog-read-help" className="text-sm text-text-2">Leave empty to estimate from the body.</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="min-w-0 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Visibility</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <Label htmlFor="blog-publish-at">Publish at</Label>
                <Input id="blog-publish-at" type="datetime-local" value={publishAt} onChange={(e) => setPublishAt(e.target.value)} aria-describedby="blog-publish-help" />
                <p id="blog-publish-help" className="text-sm text-text-2">
                  {scheduled ? 'The post goes live at this time after you schedule it.' : 'Leave empty to publish now. A future time schedules the post.'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Image</CardTitle>
              </CardHeader>
              <CardContent>
                {banner ? (
                  <div className="relative overflow-hidden rounded-md border border-border-subtle">
                    <img src={getFileUrl(banner.path)} alt="" className="aspect-video w-full object-cover" />
                    <Button
                      variant="outline"
                      size="icon-sm"
                      aria-label="Remove banner"
                      className="absolute right-2 top-2"
                      onClick={() => setBanner(null)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed border-border px-4 py-6 text-center transition-colors hover:bg-subdued focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => setGalleryOpen(true)}
                  >
                    <ImagePlus className="size-5 text-muted-foreground" aria-hidden="true" />
                    <span className="rounded-md bg-fill px-3 py-1 text-sm font-medium text-ink">Choose banner</span>
                  </button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Organization</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="blog-category">Category</Label>
                  <Input id="blog-category" value={category} maxLength={60} placeholder="e.g. Style guide" onChange={(e) => setCategory(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="blog-tags">Tags</Label>
                  <Input id="blog-tags" value={tagsText} placeholder="eid, jamdani" aria-describedby="blog-tags-help" onChange={(e) => setTagsText(e.target.value)} />
                  <p id="blog-tags-help" className="text-sm text-text-2">Separate with commas, up to {TAGS_MAX}.</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="blog-author">Author</Label>
                  <Input id="blog-author" value={authorName} maxLength={80} placeholder="Shown as “By …” on the post" onChange={(e) => setAuthorName(e.target.value)} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Search engine listing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="blog-seo-title">Page title</Label>
                  <Input id="blog-seo-title" value={seoTitle} maxLength={SEO_TITLE_MAX} placeholder={title || 'The post title'} aria-describedby="blog-seo-title-help" onChange={(e) => setSeoTitle(e.target.value)} />
                  <p id="blog-seo-title-help" className="text-sm text-text-2">{seoTitle.length}/{SEO_TITLE_MAX} · Browser tab and search results. Empty: the title.</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="blog-seo-description">Meta description</Label>
                  <Textarea
                    id="blog-seo-description"
                    value={seoDescription}
                    maxLength={SEO_DESCRIPTION_MAX}
                    rows={3}
                    placeholder={excerpt || excerptOf(html, 160) || 'What search results show under the title'}
                    aria-describedby="blog-seo-description-help"
                    onChange={(e) => setSeoDescription(e.target.value)}
                  />
                  <p id="blog-seo-description-help" className="text-sm text-text-2">{seoDescription.length}/{SEO_DESCRIPTION_MAX} · Empty: the excerpt.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <MediaGalleryModal
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onSelect={(files) => {
          const file = files[0]
          if (file) setBanner({ id: file.id, path: file.path })
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Move this post to the trash?"
        description={`"${title}" will be removed from your blog. You can restore it from the Trash tab.`}
        confirmText="Move to trash"
        variant="destructive"
        loading={remove.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  )
}
